import { useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import { useChatStore } from '../stores/useChatStore'

const SCROLL_THRESHOLD = 50

export const useScrollManagement = () => {
  const messagesContainerRef = useRef(null)
  const isScrolling = useRef(false)
  const shouldAutoScroll = useRef(true)
  // Selectors
  const isSendingMessage = useChatStore(state => state.isSendingMessage)
  const messages = useChatStore(state => state.messages)
  const userHasScrolledUp = useChatStore(state => state.userHasScrolledUp)
  const showGoToTop = useChatStore(state => state.showGoToTop)

  const setUserHasScrolledUp = useChatStore(state => state.setUserHasScrolledUp)
  const setShowGoToTop = useChatStore(state => state.setShowGoToTop)

  useEffect(() => {
    shouldAutoScroll.current = !userHasScrolledUp
  }, [userHasScrolledUp])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      if (isScrolling.current) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      const isAtBottom = distanceFromBottom <= SCROLL_THRESHOLD
      const showTop = scrollTop > clientHeight

      shouldAutoScroll.current = isAtBottom

      requestAnimationFrame(() => {
        if (isAtBottom && userHasScrolledUp) {
          setUserHasScrolledUp(false)
        } else if (!isAtBottom && !userHasScrolledUp) {
          setUserHasScrolledUp(true)
        }

        if (showTop !== showGoToTop) {
          setShowGoToTop(showTop)
        }
      })
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [userHasScrolledUp, showGoToTop, setUserHasScrolledUp, setShowGoToTop])

  useLayoutEffect(() => {
    const container = messagesContainerRef.current
    if (!container || !shouldAutoScroll.current) return

    const behavior = isSendingMessage ? 'auto' : 'smooth'

    const animationFrameId = requestAnimationFrame(() => {
      isScrolling.current = true
      container.scrollTo({
        top: container.scrollHeight,
        behavior: behavior,
      })

      setTimeout(() => {
        isScrolling.current = false
      }, 100)
    })

    return () => cancelAnimationFrame(animationFrameId)
  }, [messages, isSendingMessage])

  const scrollToBottom = useCallback(
    (behavior = 'smooth') => {
      const container = messagesContainerRef.current
      if (!container) return

      shouldAutoScroll.current = true
      setUserHasScrolledUp(false) // Reset state manually

      isScrolling.current = true
      container.scrollTo({
        top: container.scrollHeight,
        behavior: behavior,
      })

      setTimeout(() => {
        isScrolling.current = false
      }, 500)
    },
    [setUserHasScrolledUp]
  )

  const scrollToTop = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    shouldAutoScroll.current = false

    container.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return {
    messagesContainerRef,
    userHasScrolledUp,
    showGoToTop,
    scrollToBottom,
    scrollToTop,
  }
}
